import React, { useEffect, useState } from "react";
import { Modal, Button, Input, Tag, message } from "antd";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  AppDispatch,
  exportReadingList,
  exportReadingListClear,
  exportReadingListRequest,
  getExportRequestListRequest,
  ReadingFilterInput,
} from "@src/redux";
import toast from "react-hot-toast";
import { BASE_URL } from "@src/helpers/constants";

interface ReadingsReportModalProps {
  isModalVisible: boolean;
  setIsModalVisible: React.Dispatch<React.SetStateAction<boolean>>;
  // The filter behind the table this export belongs to. Unset until that table
  // has one: exporting without it would mean every reading the user can reach.
  filter?: ReadingFilterInput;
}

const ReadingsReportModal: React.FC<ReadingsReportModalProps> = ({
  isModalVisible,
  setIsModalVisible,
  filter,
}) => {
  const [emails, setEmails] = useState<string[]>([]);
  const [emailInput, setEmailInput] = useState<string>("");
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();

  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  const { result: excelExport, loading } = useSelector(exportReadingList);

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const handleAddEmail = () => {
    const trimmedEmail = emailInput.trim();
    if (emailRegex.test(trimmedEmail)) {
      if (!emails.includes(trimmedEmail)) {
        setEmails([...emails, trimmedEmail]);
        setEmailInput("");
      } else {
        message.warning(t("email_already_added"));
      }
    } else {
      message.error(t("invalid_email"));
    }
  };

  const handleRemoveEmail = (removedEmail: string) => {
    setEmails(emails.filter((email) => email !== removedEmail));
  };

  const isValidEmail = (email: string) => emailRegex.test(email.trim());

  const handleExportReadingReport = () => {
    if (!filter) return;
    dispatch(
      exportReadingListRequest({
        filter,
        data: {
          emailIdList:
            emails?.length > 0
              ? emails
              : emailInput
              ? [emailInput]
              : [],
        },
      })
    );
  };

  useEffect(() => {
    if (excelExport?.status === "success") {
      dispatch(exportReadingListClear());
      setIsModalVisible(false);
      setEmails([]);
      dispatch(
        getExportRequestListRequest({
          pagination: {pageNumber: 1, pageLimit: 10},
          sort: [{field: "requestedDatetime", order: "desc"}],
        })
      );
      toast.success(
        t(
          "Your email request has been created. You will receive the report over email within 15minutes."
        )
      );
    } else if (excelExport?.error?.message) {
      const serverMessage = excelExport.error.message;
      dispatch(exportReadingListClear());
      // Show what the server actually said. These messages are written for
      // end users ("storage is not configured... contact support", "the
      // report worker could not be started"), and collapsing them all into
      // "Something went wrong" left users and support with no way to tell an
      // outage from a misconfiguration. Fall back to the generic text only
      // for messages that are not user-facing.
      toast.error(
        serverMessage && serverMessage.length <= 300
          ? t(serverMessage)
          : t("Something went wrong")
      );
    }
  }, [excelExport]);
  return (
    <Modal
      title={
        <span className="reading-modal-title">{t("reading_modal_title")}</span>
      }
      visible={isModalVisible}
      onCancel={handleCancel}
      footer={null}
    >
      <div>
        <div style={{ display: "flex" }}>
          <Input
            value={emailInput}
            onChange={(e) => setEmailInput(e.target.value)}
            onPressEnter={handleAddEmail}
            placeholder={t("enter_emails")}
          />
          <Button
            type="primary"
            style={{ marginLeft: "10px" }}
            disabled={!filter || (emailInput ? !isValidEmail(emailInput) : false)}
            onClick={handleExportReadingReport}
            loading={loading}
          >
            {t("email")}
          </Button>
        </div>
        <div
          style={{
            display: "flex",
            gap: "10px",
            flexWrap: "wrap",
            marginTop: "5px",
          }}
        >
          {emails?.map((email) => (
            <Tag
              key={email}
              closable
              onClose={() => handleRemoveEmail(email)}
              style={{ backgroundColor: "#319795", color: "#fff" }}
              className="mail-clear-tag"
            >
              {email}
            </Tag>
          ))}
        </div>
      </div>
    </Modal>
  );
};

export default ReadingsReportModal;
